import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";

const emptyPromo = { title: "", code: "", discount: "", description: "", valid_from: "", valid_to: "", active: true };

export const PromotionsTab = () => {
  const [promos, setPromos] = useState([]);
  const [form, setForm] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/promotions").then((res) => setPromos(res.data)).catch(() => toast.error("Could not load promotions"));
  }, []);

  useEffect(load, [load]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    try {
      if (form.id) await api.put(`/admin/promotions/${form.id}`, form);
      else await api.post("/admin/promotions", form);
      toast.success(form.id ? "Promotion updated" : "Promotion created");
      setForm(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Save failed"));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/promotions/${id}`);
      toast.success("Promotion deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const toggle = async (p) => {
    try {
      await api.put(`/admin/promotions/${p.id}`, { ...p, active: !p.active });
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const inputCls =
    "bg-white border border-[#0D1B2A]/20 focus:border-[#C5A059] focus:ring-2 focus:ring-[#C5A059]/30 rounded-xl px-4 py-2.5 text-[#0D1B2A] placeholder:text-gray-400 outline-none w-full transition-all text-sm";

  return (
    <div data-testid="promotions-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif-display text-2xl font-semibold text-[#0A192F]">Promotions & Discount Rates</h2>
        {!form && (
          <button onClick={() => setForm({ ...emptyPromo })} className="flex items-center gap-2 bg-[#0A192F] text-[#F8F6F0] hover:bg-[#C5A059] hover:text-[#0A192F] rounded-full px-5 py-2.5 text-sm font-semibold transition-all" data-testid="promo-add-button">
            <Plus size={15} /> New Promotion
          </button>
        )}
      </div>

      {form && (
        <form onSubmit={save} className="bg-[#0A192F] rounded-2xl p-6 sm:p-8 mb-8 space-y-4" data-testid="promo-form">
          <div className="flex items-center justify-between">
            <h3 className="font-serif-display text-xl text-[#F8F6F0]">{form.id ? "Edit Promotion" : "New Promotion"}</h3>
            <button type="button" onClick={() => setForm(null)} className="text-[#F8F6F0]/60 hover:text-[#C5A059]" data-testid="promo-form-cancel" aria-label="Cancel"><X size={18} /></button>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <input required placeholder="Title *" value={form.title} onChange={set("title")} className={inputCls} data-testid="promo-title-input" />
            <input required placeholder="Promo code * (e.g. LAKE20)" value={form.code} onChange={set("code")} className={inputCls} data-testid="promo-code-input" />
            <input required placeholder="Discount * (e.g. 20% OFF)" value={form.discount} onChange={set("discount")} className={inputCls} data-testid="promo-discount-input" />
          </div>
          <textarea placeholder="Description" value={form.description} onChange={set("description")} rows={2} className={inputCls} data-testid="promo-description-input" />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[#F8F6F0]/50 block mb-1">Valid from</label>
              <input type="date" value={form.valid_from} onChange={set("valid_from")} className={inputCls} data-testid="promo-valid-from-input" />
            </div>
            <div>
              <label className="text-xs text-[#F8F6F0]/50 block mb-1">Valid to</label>
              <input type="date" value={form.valid_to} onChange={set("valid_to")} className={inputCls} data-testid="promo-valid-to-input" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-[#F8F6F0]/80" data-testid="promo-active-label">
            <input type="checkbox" checked={form.active} onChange={set("active")} className="accent-[#C5A059] w-4 h-4" data-testid="promo-active-checkbox" /> Active (visible to guests)
          </label>
          <button type="submit" className="bg-[#C5A059] text-[#0A192F] hover:bg-[#D4AF37] rounded-full px-8 py-3 font-semibold text-sm transition-all" data-testid="promo-save-button">
            Save Promotion
          </button>
        </form>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        {promos.map((p) => (
          <div key={p.id} className={`bg-[#FAF8F5] border rounded-2xl p-6 ${p.active ? "border-[#C5A059]/40" : "border-[#0D1B2A]/10 opacity-60"}`} data-testid={`promo-card-${p.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <span className="text-[#C5A059] text-xs font-bold uppercase tracking-widest">{p.discount}</span>
                <h3 className="font-serif-display text-xl font-semibold text-[#0A192F] mt-1">{p.title}</h3>
                <p className="font-mono text-xs tracking-widest text-[#0D1B2A]/60 mt-1">{p.code}</p>
                <p className="text-[#4A5568] text-sm mt-2">{p.description}</p>
              </div>
              <div className="flex gap-1 shrink-0">
                <button onClick={() => setForm({ ...p })} className="p-2 text-[#0D1B2A]/50 hover:text-[#C5A059] transition-colors" data-testid={`promo-edit-${p.id}`} aria-label="Edit"><Pencil size={15} /></button>
                <button onClick={() => remove(p.id)} className="p-2 text-red-400 hover:text-red-600 transition-colors" data-testid={`promo-delete-${p.id}`} aria-label="Delete"><Trash2 size={15} /></button>
              </div>
            </div>
            <button
              onClick={() => toggle(p)}
              className={`mt-4 text-xs font-semibold rounded-full px-4 py-2 transition-all ${p.active ? "bg-emerald-500/10 text-emerald-700 border border-emerald-300" : "bg-[#0D1B2A]/5 text-[#0D1B2A]/50 border border-[#0D1B2A]/15"}`}
              data-testid={`promo-toggle-${p.id}`}
            >
              {p.active ? "Active — click to hide" : "Hidden — click to publish"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
